/** @jsxImportSource @emotion/react */
import { Header } from '@quickbit/backoffice-core-frontend';
import {
  Card,
  Icon,
  Loader,
  Table,
  Typography,
  useTheme,
} from '@quickbit/qb-design-system';
import { useNavigate, useParams } from 'react-router-dom';
import { CustodianUserStatus, useUserDetails } from 'api/useDirectOnrampApi';
import { KycDocumentImages } from 'components';
import {
  getKycDocumentImage,
  getKycDocumentImages,
} from 'tabs/NewSkinning/SkinningUsers/api';
import { DirectOnrampUserDetailsField } from './DirectOnrampUserDetailsField';

export const DirectOnrampUserDetails = () => {
  const { palette, spacing } = useTheme();
  const navigate = useNavigate();
  const { userId } = useParams();

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const { data, isLoading } = useUserDetails(userId!);

  let walletAddress: string = '';
  let emailAddress: string = '';
  let balance: number = 0;
  let euroBalance: number = 0;
  let status: CustodianUserStatus = CustodianUserStatus.ACTIVE;

  if (!isLoading && data) {
    walletAddress = data.walletAddress;
    emailAddress = data.emailAddress;
    balance = data.balance;
    euroBalance = data.euroBalance;
    status = data.status;
  }

  return (
    <>
      <Header>
        <div
          css={{
            display: 'flex',
            gap: spacing.xxxs,
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div
            css={{ display: 'flex', gap: spacing.xxxs, alignItems: 'center' }}
          >
            <div
              css={{
                cursor: 'pointer',
                ':hover': {
                  textDecoration: 'underline',
                  textDecorationColor: palette.grey.x300,
                },
              }}
              onClick={() => navigate('/direct-onramp/users')}
            >
              <Typography variant="subtitle" color={palette.grey.x300}>
                Users
              </Typography>
            </div>
            <div
              css={{
                transform: 'rotate(180deg)',
                svg: {
                  fill: 'none',
                  stroke: palette.grey.x300,
                },
              }}
            >
              <Icon name="chevron" />
            </div>
            <Typography variant="subtitle">{userId}</Typography>
          </div>
        </div>
      </Header>
      {isLoading && <Loader />}
      {data && (
        <div>
          <div
            css={{
              display: 'grid',
              gap: spacing.xxs,
              gridTemplateColumns: 'repeat(2, 1fr)',
            }}
          >
            <DirectOnrampUserDetailsField fieldName="userId" value={userId} />
            <DirectOnrampUserDetailsField
              fieldName="emailAddress"
              value={emailAddress}
            />
            <DirectOnrampUserDetailsField
              fieldName="walletAddress"
              value={walletAddress}
            />
            <DirectOnrampUserDetailsField fieldName="balance" value={balance} />
            <DirectOnrampUserDetailsField
              fieldName="euroBalance"
              value={euroBalance}
            />
            <DirectOnrampUserDetailsField fieldName="status" value={status} />
          </div>
          <div css={{ marginTop: spacing.s }}>
            {data.transactionRequests.length > 0 && (
              <Card>
                <Table
                  headers={[
                    {
                      label: 'Transaction Request Reference Id',
                      cellKey: 'referenceId',
                      sort: false,
                    },
                  ]}
                  rows={data.transactionRequests.map((referenceId) => ({
                    value: {
                      referenceId,
                    },
                  }))}
                  onRowClick={(row) => {
                    void navigate(
                      // eslint-disable-next-line @typescript-eslint/no-base-to-string, @typescript-eslint/restrict-template-expressions
                      `/direct-onramp/transaction-requests/${row.referenceId}`
                    );
                  }}
                />
              </Card>
            )}
          </div>
        </div>
      )}
      {userId && (
        <KycDocumentImages
          userId={userId}
          getKycDocumentImage={getKycDocumentImage}
          getKycDocumentImages={getKycDocumentImages}
        />
      )}
    </>
  );
};
